import { t } from '$i18n/i18n'
import { get } from 'svelte/store'
import dayjs from 'dayjs'
import { DurationFormat } from '@formatjs/intl-durationformat'

export const inputFieldFormats = {
	EXPANDED: { id: 'expanded', maxWidth: '104px' },
	NORMAL: { id: 'normal', maxWidth: '70px' },
	COMPACT: { id: 'compact', maxWidth: '45px' },
	FULL: { id: 'full', maxWidth: '100%' },
	SEARCH: { id: 'search', maxWidth: '255px' }
}

const dateFormatOptions = {
	year: 'numeric',
	month: '2-digit',
	day: '2-digit'
} satisfies Intl.DateTimeFormatOptions

const dateTimeFormatOptions = {
	year: 'numeric',
	month: 'long',
	day: 'numeric',
	hour: '2-digit',
	minute: '2-digit'
} satisfies Intl.DateTimeFormatOptions

export function formatIsoDateString(isoDateString: string, formatOptions: Intl.DateTimeFormatOptions = dateFormatOptions) {
	if (!isoDateString || isoDateString.length == 0) return ''
	return formatDate(new Date(isoDateString), formatOptions)
}

export function formatIsoDateTime(
	isoDateTime: string,
	formatOptions: Intl.DateTimeFormatOptions = dateTimeFormatOptions
) {
	if (!isoDateTime || isoDateTime.length == 0) return ''
	return new Date(isoDateTime).toLocaleString('de-DE', formatOptions)
}

function formatDate(
	date: Date | null | undefined,
	formatOptions: Intl.DateTimeFormatOptions = dateFormatOptions
) {
	if (!date) return ''
	return date.toLocaleDateString('de-DE', formatOptions)
}

export function formatNumber(number: string | number | null) {
	if (number === null) return ''
	const parsed = typeof number === 'string' ? parseFloat(number) : number
	if (isNaN(parsed)) {
		return '&nbsp;-&nbsp;'
	} else {
		return parsed.toLocaleString('de-DE', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
			useGrouping: true
		})
	}
}

export const enum Unit {
	TON = 't',
	KM = 'km',
	EUR = '€',
	EUR_PER_TON = '€/t',
	PERCENT = '%',
	KG = 'kg', // used, e.g., for emission
	OTHERS_WHOLE_NUMBERS = 'others', // this is for like 'Mandanten' or 'Personen' and returns only complete values
	NONE = 'NONE', // either using no unit or handling the unit by yourself
	M = 'm',
	M2 = 'm²',
	KM2 = 'km²'
}

/**
 * We do banker's rounding and round to maximum maxRoundDecimals decimals.
 * If all the decimals are 0, we show no decimals.
 * Everything that is not a number will be handled as a dash ('—').
 * @param num the number to be rounded
 * @param unit the unit to be shown concatenated to the number or none if Unit.NONE or Unit.OTHERS.
 * When using Unit.OTHERS, we always round to whole numbers and do not show decimals.
 * @param maxRoundDecimals the maximal amount of digits to be displayed
 * @param showUnit we can turn on the functionality to show the unit appended to the number with space
 */
export function roundToNearest(
	num: number,
	unit: Unit,
	maxRoundDecimals: number | null = null,
	showUnit: boolean = true
) {
	if (isNaN(num) || typeof num !== 'number') {
		return '—'
	}
	const unitSuffix =
		unit !== Unit.OTHERS_WHOLE_NUMBERS && unit !== Unit.NONE && showUnit ? ` ${unit}` : ''
	if (num === 0) {
		return '0' + unitSuffix
	}
	if (maxRoundDecimals === 0) {
		return (
			Math.round(num).toLocaleString('de-DE', {
				minimumFractionDigits: 0,
				maximumFractionDigits: 0,
				useGrouping: true
			}) + unitSuffix
		)
	}
	if (!maxRoundDecimals) {
		switch (unit) {
			case Unit.PERCENT:
				maxRoundDecimals = 1
				break
			case Unit.OTHERS_WHOLE_NUMBERS:
				maxRoundDecimals = 0
				break
			default:
				maxRoundDecimals = 2
		}
	}

	const pow = Math.pow(10, maxRoundDecimals)
	const roundedNumber = Math.round(num * pow) / pow

	const hasSignificantDecimals = (roundedNumber * pow) % pow !== 0

	// get amount of decimals for the rounded string depending on the unit and last decimal
	let minDecimals: number
	let maxDecimals: number

	if (!hasSignificantDecimals || unit === Unit.OTHERS_WHOLE_NUMBERS) {
		// no decimals or unit like 'Mandanten'
		minDecimals = 0
		maxDecimals = 0
	} else {
		// has decimals
		if (unit === Unit.PERCENT) {
			// we want to show only one decimal for percentage numbers
			minDecimals = 1
			maxDecimals = Math.max(maxRoundDecimals, minDecimals)
		} else {
			minDecimals = 2
			maxDecimals = Math.max(maxRoundDecimals, minDecimals)
		}
	}
	return (
		roundedNumber.toLocaleString('de-DE', {
			minimumFractionDigits: minDecimals,
			maximumFractionDigits: maxDecimals,
			useGrouping: true
		}) + unitSuffix
	)
}

export function checkNumber(number: number) {
	if (!number) {
		return
	}
	if (isNaN(number)) {
		return 0
	}
	return number
}

export function formatVolume(volume: string, showUnit: boolean = false, fractions: number = 2) {
	const parsed = parseFloat(volume)
	if (isNaN(parsed)) {
		return '&nbsp;-&nbsp;'
	} else {
		return (
			parsed.toLocaleString('de-DE', {
				minimumFractionDigits: fractions,
				maximumFractionDigits: fractions
			}) + (showUnit ? '&nbsp;' + get(t)('UI.unit.ton.short.small') : '')
		)
	}
}

export function markFieldLabelRequired(label: string): string {
	if (label == null) {
		return ''
	}

	if (!label.endsWith('*')) {
		return label + '*'
	}
	return label
}

export function localDateToUnixNumber(date: string | null): number | null {
	if (!date) {
		return null
	}
	return dayjs(date, 'YYYY-MM-DD').unix()
}

export function unixToLocalDate(unixDate: number | null): string {
	if (!unixDate) {
		return ''
	}
	return dayjs.unix(unixDate).format('DD.MM.YYYY')
}

export function unixToLocalTime(unixDate: number | null): string {
	if (!unixDate) {
		return ''
	}
	return dayjs.unix(unixDate).format('HH:mm:ss')
}

export function unixToIsoDate(unixDate: number | null): string {
	if (!unixDate) {
		return ''
	}
	return dayjs.unix(unixDate).add(12, 'h').toISOString()
}

export function formatDuration(
	minutes: number,
	locale: string = 'de-DE',
	style: 'long' | 'short' | 'narrow' | 'digital' | undefined = 'narrow'
): string {
	const hours = Math.floor(minutes / 60)
	const minutesLeft = Math.floor(minutes % 60)

	const loc = typeof navigator !== 'undefined' && navigator.language ? navigator.language : locale

	const df = new DurationFormat(loc, { style })

	return df.format({
		hours: hours || undefined,
		minutes: minutesLeft
	})
}
